const GRANT_KEYS = [
  "capabilities",
  "acls",
  "acl",
  "permissions",
  "role_permissions",
  "rolePermissions",
];

const normalizeCapability = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s.-]+/g, "_");

export const safeParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const grantName = (grant) => {
  if (typeof grant === "string") return normalizeCapability(grant);
  if (!grant || typeof grant !== "object") return "";

  return normalizeCapability(
    grant.name ||
      grant.key ||
      grant.code ||
      grant.slug ||
      grant.acl_name ||
      grant.capability ||
      grant.permission_name ||
      grant.permission?.name ||
      grant.permission,
  );
};

const grantAllowed = (grant) => {
  if (!grant || typeof grant !== "object") return true;
  const allowed = grant.allowed ?? grant.enabled ?? grant.granted ?? grant.pivot?.allowed;
  const moduleStatus = grant.module_status ?? grant.pivot?.module_status;
  const isDenied = (value) => value === false || value === 0 || value === "0";
  return !isDenied(allowed) && !isDenied(moduleStatus);
};

const collectGrantContainers = (session) => {
  const userDetails = session?.userDetails || session?.user || null;
  const currentCompany = session?.currentCompany || null;
  const role = currentCompany?.pivot?.role || currentCompany?.role || null;
  const candidates = [
    userDetails,
    userDetails?.user,
    currentCompany,
    currentCompany?.pivot,
    role,
  ].filter(Boolean);

  const containers = [];
  candidates.forEach((candidate) => {
    GRANT_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(candidate, key)) {
        containers.push(candidate[key]);
      }
    });
  });
  return containers;
};

const flattenGrants = (containers) =>
  containers.flatMap((container) => {
    if (Array.isArray(container)) return container;
    if (container && typeof container === "object") {
      return Object.entries(container).map(([name, value]) =>
        typeof value === "object" ? { name, ...value } : { name, allowed: value },
      );
    }
    return [];
  });

export const readAccessSession = () => ({
  userDetails: safeParse(localStorage.getItem("user")),
  currentCompany: safeParse(localStorage.getItem("currentCompany")),
});

export const isCompanyOwner = (session = readAccessSession()) => {
  const pivot = session?.currentCompany?.pivot;
  return pivot?.is_owner === 1 || pivot?.is_owner === true;
};

// Role names treated as full-access. Backends have shipped roles named "admin"
// with an EMPTY permissions array (verified on the dev API), which would
// otherwise fail closed to zero accessible pages — an admin locked out of the
// whole app. Owner/admin/super-admin/operator are equivalent for menu and route
// access; any other role name still filters on its explicit grants.
const FULL_ACCESS_ROLE_NAMES = new Set([
  "owner",
  "admin",
  "super_admin",
  "superadmin",
  "operator",
]);

export const isSuperAdminUser = (session = readAccessSession()) => {
  const superAdminEmail = process.env.VUE_APP_SUPER_ADMIN_EMAIL;
  const email = session?.userDetails?.user?.email;
  // Both sides must be present — two undefineds must never compare equal.
  return Boolean(superAdminEmail && email && email === superAdminEmail);
};

export const roleName = (session = readAccessSession()) =>
  normalizeCapability(
    session?.currentCompany?.pivot?.role?.name ||
      session?.currentCompany?.role?.name,
  );

// True for company owners, admin-equivalent roles, and the super-admin account.
// These identities bypass the explicit-grant check entirely; note this governs
// page/menu access only — `isTeamMember` opts out so the internal Agent
// profile/prompt stays team-only per CLAUDE.md.
export const hasFullAccessRole = (session = readAccessSession()) =>
  isCompanyOwner(session) ||
  isSuperAdminUser(session) ||
  FULL_ACCESS_ROLE_NAMES.has(roleName(session));

export const getAccessSnapshot = (session = readAccessSession()) => {
  const containers = collectGrantContainers(session);
  const grants = flattenGrants(containers);
  const granted = new Set(
    grants.filter(grantAllowed).map(grantName).filter(Boolean),
  );

  return {
    hasExplicitGrants: containers.length > 0,
    granted,
  };
};

export const hasCapability = (
  capability,
  session = readAccessSession(),
  { legacyDefault = true, aliases = [], allowOwnerBypass = true } = {},
) => {
  if (!capability) return true;
  if (allowOwnerBypass && hasFullAccessRole(session)) return true;
  const { hasExplicitGrants, granted } = getAccessSnapshot(session);
  if (!hasExplicitGrants) return legacyDefault;

  return [capability, ...aliases]
    .map(normalizeCapability)
    .some((name) => granted.has(name));
};

export const isTeamMember = (session = readAccessSession()) =>
  hasCapability("agent_internal_profile_write", session, {
    legacyDefault: false,
    aliases: ["agent_prompt_write", "team_agent_profile_write"],
    allowOwnerBypass: false,
  });

export { normalizeCapability };
